import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Copy, X, AlertTriangle } from "lucide-react";

// Iter 80 — Shared payout detail modal used by both Payouts Pending and
// Payouts Done. Read + Edit + Audit in one panel. All fields go through the
// admin endpoint `/admin/payouts/{id}`; `reason` is required when amount,
// currency, or wallet is changed.
function Pill({ tone = "default", children }) {
  const tones = {
    default: "border-white/20 text-white",
    warn: "border-[#F5B841]/50 text-[#F5B841]",
    ok: "border-emerald-500/50 text-emerald-400",
    danger: "border-[#F26D6D]/60 text-[#F26D6D]",
  };
  return <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${tones[tone]}`}>{children}</span>;
}

const CHANGES_REQUIRING_REASON = ["amount", "currency", "walletAddressSnapshot"];

// Iter 87 — MANUAL sanctions-screening attestation panel for loyalty_rebate
// payouts. Admin has just checked the recipient wallet in a real sanctions
// tool; this panel records what they saw. `clear` unlocks Mark Paid;
// `flagged` blocks it (distinct red). Automated stub screening does not
// gate this flow.
function ManualScreeningPanel({ payoutId, doc, isPaid, busy, setBusy, onSaved, refresh }) {
  const existing = doc?.screening_manual || {};
  const [source, setSource] = useState(existing.source || "");
  const [result, setResult] = useState(existing.result || "");
  const [note, setNote] = useState(existing.note || "");
  useEffect(() => {
    setSource(existing.source || "");
    setResult(existing.result || "");
    setNote(existing.note || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutId, existing.checked_at]);

  const tone = existing.result === "clear" ? "ok" : existing.result === "flagged" ? "danger" : "warn";
  const label = existing.result ? existing.result[0].toUpperCase() + existing.result.slice(1) : "Not recorded";
  const flagged = existing.result === "flagged";

  const submit = async () => {
    if (!source.trim()) { toast.error("Source (e.g. Chainalysis, OFAC SDN) is required"); return; }
    if (result !== "clear" && result !== "flagged") { toast.error("Pick a result: clear or flagged"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/payouts/${payoutId}/attest-screening`, { source: source.trim(), result, note: note.trim() });
      toast.success("Screening attestation recorded");
      await refresh();
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`border ${flagged ? "border-[#F26D6D]/60 bg-[#F26D6D]/5" : "border-white/10"} p-3 space-y-3`} data-testid="payout-detail-manual-screening">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Manual sanctions screening (cashback)</div>
        <div data-testid="payout-detail-manual-screening-status"><Pill tone={tone}>{label}</Pill></div>
      </div>
      {existing.checked_at && (
        <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
          <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Checked by</span><div data-testid="payout-detail-manual-checked-by">{(existing.checked_by || "").slice(0, 8) || "—"}</div></div>
          <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Checked at</span><div data-testid="payout-detail-manual-checked-at">{existing.checked_at}</div></div>
          <div className="col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Source</span><div data-testid="payout-detail-manual-source">{existing.source || "—"}</div></div>
          {existing.note && <div className="col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Note</span><div className="text-[#8B97AE]" data-testid="payout-detail-manual-note">{existing.note}</div></div>}
        </div>
      )}
      {flagged && (
        <div className="text-[11px] font-mono text-[#F26D6D]" data-testid="payout-detail-manual-flagged-note">
          Wallet was flagged. Payout cannot be marked paid.
        </div>
      )}
      {!isPaid && (
        <div className="border border-white/10 p-3 space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Record screening check</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Source (e.g. Chainalysis, OFAC SDN)</Label>
              <Input data-testid="payout-detail-manual-source-input" value={source} onChange={(e) => setSource(e.target.value)} className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Result</Label>
              <Select value={result} onValueChange={(v) => setResult(v)}>
                <SelectTrigger data-testid="payout-detail-manual-result" className="rounded-none bg-transparent border-white/20 h-9 font-mono"><SelectValue placeholder="pick…" /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">
                  <SelectItem value="clear">clear</SelectItem>
                  <SelectItem value="flagged">flagged</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Note (optional)</Label>
            <Input data-testid="payout-detail-manual-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
          </div>
          <Button data-testid="payout-detail-manual-submit" onClick={submit} disabled={busy || !source.trim() || !result} className="rounded-none bg-[#F5B841] text-[#04150F] hover:bg-[#F5B841]/80 h-8 font-mono text-[11px] uppercase tracking-widest disabled:opacity-40">Record screening check</Button>
        </div>
      )}
    </div>
  );
}

export default function PayoutDetailModal({ payoutId, onClose, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");

  const refresh = async () => {
    if (!payoutId) return;
    try {
      const r = await api.get(`/admin/payouts/${payoutId}`);
      setDoc(r.data);
      setForm({
        amount: r.data.amount ?? "",
        currency: r.data.currency ?? "USDT",
        walletAddressSnapshot: r.data.walletAddressSnapshot ?? "",
        txReference: r.data.txReference ?? "",
        status: r.data.status ?? "pending",
      });
      setReason("");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [payoutId]);

  if (!payoutId) return null;

  const changedFields = () => {
    if (!doc) return [];
    const out = [];
    for (const k of ["amount", "currency", "walletAddressSnapshot", "txReference", "status"]) {
      const cur = form[k];
      const prev = doc[k] ?? (k === "status" ? "pending" : "");
      const equal = k === "amount"
        ? Number(cur || 0) === Number(prev || 0)
        : String(cur ?? "") === String(prev ?? "");
      if (!equal) out.push(k);
    }
    return out;
  };
  const changes = changedFields();
  const reasonNeeded = changes.some((k) => CHANGES_REQUIRING_REASON.includes(k));

  const copyWallet = async () => {
    try {
      await navigator.clipboard.writeText(doc?.walletAddressSnapshot || "");
      toast.success("Wallet copied");
    } catch { /* noop */ }
  };

  const save = async () => {
    if (!doc) return;
    const patch = {};
    for (const k of changes) patch[k] = form[k];
    if (Object.keys(patch).length === 0) { toast.info("No changes"); return; }
    if (reasonNeeded && !reason.trim()) { toast.error("Reason is required for amount / currency / wallet changes"); return; }
    if (reasonNeeded) patch.reason = reason.trim();
    setBusy(true);
    try {
      await api.patch(`/admin/payouts/${payoutId}`, patch);
      toast.success("Saved");
      await refresh();
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    if (!form.txReference?.trim()) { toast.error("Fill txReference first"); return; }
    setBusy(true);
    try {
      const patch = { status: "paid" };
      // Include any tx_ref edit in the same PATCH so the guardrail passes on first try.
      if ((form.txReference || "") !== (doc.txReference || "")) patch.txReference = form.txReference;
      await api.patch(`/admin/payouts/${payoutId}`, patch);
      toast.success("Marked paid");
      await refresh();
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };
  const markFailed = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/payouts/${payoutId}`, { status: "failed" });
      toast.success("Marked failed");
      await refresh();
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  const isPaid = doc?.status === "paid";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="payout-detail-modal" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#0B1220] border border-white/20 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Payout detail</div>
            <div className="font-display font-bold text-lg text-[#E8ECF4]" data-testid="payout-detail-id">{payoutId?.slice(0, 8)}</div>
          </div>
          <button data-testid="payout-detail-close" onClick={onClose} className="text-[#8B97AE] hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!doc ? (
          <div className="p-6 text-xs text-[#8B97AE]">…</div>
        ) : (
          <div className="p-4 space-y-5">
            {doc.is_test && (
              <div className="flex" data-testid="payout-detail-is-test">
                <Pill tone="warn">TEST</Pill>
              </div>
            )}
            {/* READ SECTION */}
            <div className="grid grid-cols-2 gap-3 text-xs font-mono">
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Type</div><div data-testid="payout-detail-type">{doc.type}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Auto</div><div data-testid="payout-detail-auto">{doc.autoCreated ? "yes" : "no"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Recipient</div><div data-testid="payout-detail-recipient"><span className="text-[#E8ECF4]">{doc.recipient_name || "—"}</span>{doc.recipient_email && <div className="text-[10px] text-[#8B97AE]">{doc.recipient_email}</div>}<div className="text-[10px] text-[#8B97AE] truncate">{doc.playerId || doc.streamerId || "—"}</div></div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Referral</div><div data-testid="payout-detail-referral">{doc.referralId ? doc.referralId.slice(0, 8) : "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Currency · Chain</div><div data-testid="payout-detail-chain"><span className="text-[#E8ECF4]">{doc.currency || "—"}</span> · <span className="text-[#8B97AE]">{doc.chain || doc.walletNetworkSnapshot || "—"}</span></div></div>
              <div />
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Created</div><div data-testid="payout-detail-created">{doc.createdAt || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Paid at</div><div data-testid="payout-detail-paid-at">{doc.paidAt || "—"}</div></div>
            </div>

            {/* SCREENING BLOCK — Iter 81 automated (non-rebate) OR Iter 87 manual (loyalty_rebate) */}
            {doc.type === "loyalty_rebate" ? (
              <ManualScreeningPanel payoutId={payoutId} doc={doc} isPaid={isPaid} busy={busy} setBusy={setBusy} onSaved={onSaved} refresh={refresh} />
            ) : (() => {
              const st = doc.screening_status || "not_run";
              const tone = st === "clear" ? "ok" : st === "flagged" ? "danger" : st === "error" ? "danger" : "warn";
              const label = st === "clear" ? "Clear" : st === "flagged" ? "Flagged" : st === "error" ? "Error" : "Not run";
              const runScreening = async () => {
                setBusy(true);
                try { await api.post(`/admin/payouts/${payoutId}/screen`); toast.success("Screening run"); await refresh(); onSaved && onSaved(); }
                catch (e) { toast.error(apiErrorMessage(e)); }
                finally { setBusy(false); }
              };
              return (
                <div className={`border ${st === "flagged" ? "border-[#F26D6D]/60 bg-[#F26D6D]/5" : "border-white/10"} p-3 space-y-2`} data-testid="payout-detail-screening">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Sanctions / wallet screening</div>
                    <div data-testid="payout-detail-screening-status"><Pill tone={tone}>{label}</Pill></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[11px] font-mono">
                    <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Provider</span><div data-testid="payout-detail-screening-provider">{doc.screening_provider || "—"}</div></div>
                    <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Checked at</span><div data-testid="payout-detail-screening-when">{doc.screening_checked_at || "—"}</div></div>
                  </div>
                  {st === "flagged" && (
                    <div className="text-[11px] font-mono text-[#F26D6D]" data-testid="payout-detail-screening-flagged-note">
                      This wallet is flagged. Payout cannot be marked paid.
                    </div>
                  )}
                  <div>
                    <Button data-testid="payout-detail-screening-run" onClick={runScreening} disabled={busy || isPaid} className="rounded-none bg-transparent border border-[#F5B841] text-[#F5B841] hover:bg-[#F5B841]/10 h-8 font-mono text-[11px] uppercase tracking-widest">Run screening</Button>
                  </div>
                </div>
              );
            })()}

            {/* EDIT SECTION */}
            <div className="space-y-3 border-t border-white/10 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Amount</Label>
                  <Input data-testid="payout-detail-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} disabled={isPaid} className="rounded-none bg-transparent border-white/20 h-9 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))} disabled={isPaid}>
                    <SelectTrigger data-testid="payout-detail-currency" className="rounded-none bg-transparent border-white/20 h-9 font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">
                      <SelectItem value="USDT">USDT</SelectItem>
                      <SelectItem value="USDC">USDC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Wallet (public receiving address)</Label>
                {/* Iter 132 — recipient's on-file wallet: only auto-fill when the
                    payout's coin+chain match. A mismatch is a hard warning —
                    admin must NEVER pay to a wrong-chain address (irreversible
                    loss). Only PUBLIC receiving addresses are exposed. */}
                {doc.recipient_wallet && (() => {
                  const rw = doc.recipient_wallet;
                  if (rw.matches_payout && rw.address) {
                    const canFill = form.walletAddressSnapshot !== rw.address;
                    return (
                      <div className="mb-2 border border-[#2DD4A7]/40 bg-[#2DD4A7]/[0.05] p-2 flex flex-wrap items-center gap-2 text-[10px] font-mono" data-testid="payout-detail-recipient-wallet">
                        <span className="uppercase tracking-widest text-[#2DD4A7]">On-file wallet</span>
                        <span className="text-[#8B97AE]">{rw.coin} · {rw.network}</span>
                        <span className="truncate max-w-[220px] text-[#E8ECF4]" data-testid="payout-detail-recipient-wallet-address">{rw.address}</span>
                        <button type="button"
                          data-testid="payout-detail-wallet-autofill"
                          disabled={isPaid || !canFill}
                          onClick={() => setForm((f) => ({ ...f, walletAddressSnapshot: rw.address }))}
                          className="ml-auto px-2 h-7 border border-[#2DD4A7]/60 text-[#2DD4A7] hover:bg-[#2DD4A7]/10 uppercase tracking-widest disabled:opacity-40">
                          {canFill ? "Use on-file address" : "Loaded"}
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div className="mb-2 border border-[#F5B841]/60 bg-[#F5B841]/[0.08] p-2 text-[10px] font-mono space-y-1" data-testid="payout-detail-wallet-mismatch">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 text-[#F5B841]" />
                        <span className="uppercase tracking-widest text-[#F5B841]">Wallet mismatch — do NOT auto-fill</span>
                      </div>
                      <div className="text-[#E8ECF4]">
                        Payout chain: <span className="text-[#F5B841]">{rw.payout_coin || "—"} · {rw.payout_network || "—"}</span>
                        &nbsp;·&nbsp;On-file wallet: <span className="text-[#8B97AE]">{rw.coin || "—"} · {rw.network || "—"}</span>
                      </div>
                      <div className="text-[#8B97AE]">No matching address on file. Confirm the correct address with the recipient before paying — a wrong-chain send is irreversible.</div>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2">
                  <Input data-testid="payout-detail-wallet" value={form.walletAddressSnapshot} onChange={(e) => setForm((f) => ({ ...f, walletAddressSnapshot: e.target.value }))} disabled={isPaid} className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
                  <Button type="button" data-testid="payout-detail-wallet-copy" onClick={copyWallet} className="rounded-none bg-transparent border border-white/20 text-[#8B97AE] hover:text-white h-9 px-2"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Tx reference</Label>
                  <Input data-testid="payout-detail-tx" value={form.txReference} onChange={(e) => setForm((f) => ({ ...f, txReference: e.target.value }))} disabled={isPaid} className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))} disabled={isPaid}>
                    <SelectTrigger data-testid="payout-detail-status" className="rounded-none bg-transparent border-white/20 h-9 font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">
                      <SelectItem value="pending">pending</SelectItem>
                      <SelectItem value="due">due</SelectItem>
                      <SelectItem value="processing">processing</SelectItem>
                      <SelectItem value="paid">paid</SelectItem>
                      <SelectItem value="failed">failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {reasonNeeded && (
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Reason (required — amount / currency / wallet change)</Label>
                  <Input data-testid="payout-detail-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none bg-transparent border-[#F5B841]/50 h-9 font-mono text-xs" />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Button data-testid="payout-detail-save" onClick={save} disabled={busy || isPaid || changes.length === 0} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-9 font-mono text-[11px] uppercase tracking-widest">Save</Button>
                {(doc.status === "pending" || doc.status === "due") && (
                  <>
                    <Button
                      data-testid="payout-detail-mark-paid"
                      onClick={markPaid}
                      disabled={busy || !form.txReference?.trim() || (doc.type === "loyalty_rebate" ? (doc.screening_manual?.result !== "clear") : ((doc.screening_status || "not_run") !== "clear"))}
                      title={doc.type === "loyalty_rebate"
                        ? (doc.screening_manual?.result !== "clear" ? "Record a 'clear' manual screening attestation before paying" : (!form.txReference?.trim() ? "Fill txReference before paying" : ""))
                        : ((doc.screening_status || "not_run") !== "clear" ? "Screening must be clear before payout can be marked paid" : "")}
                      className="rounded-none bg-emerald-500 text-black hover:bg-emerald-400 h-9 font-mono text-[11px] uppercase tracking-widest disabled:opacity-40"
                    >Mark Paid</Button>
                    <Button data-testid="payout-detail-mark-failed" onClick={markFailed} disabled={busy} className="rounded-none bg-transparent border border-[#F26D6D] text-[#F26D6D] hover:bg-[#F26D6D]/10 h-9 font-mono text-[11px] uppercase tracking-widest">Mark Failed</Button>
                  </>
                )}
              </div>
            </div>

            {/* AUDIT TRAIL */}
            <div className="border-t border-white/10 pt-4" data-testid="payout-detail-audit">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Audit trail</div>
              {(!doc.audit || doc.audit.length === 0) ? (
                <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="payout-detail-audit-empty">No audit entries yet.</div>
              ) : (
                <ul className="space-y-2">
                  {[...doc.audit].reverse().map((a, i) => (
                    <li key={i} className="border border-white/10 p-2 text-[11px] font-mono" data-testid={`payout-detail-audit-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="uppercase tracking-widest text-[#F5B841]">{a.field}</span>
                        <span className="text-[10px] text-[#8B97AE]">{a.timestamp}</span>
                      </div>
                      <div className="text-[#E8ECF4] mt-1">
                        <span className="text-[#F26D6D]">{String(a.before)}</span>
                        <span className="text-[#8B97AE] mx-2">→</span>
                        <span className="text-emerald-400">{String(a.after)}</span>
                      </div>
                      <div className="text-[10px] text-[#8B97AE] mt-1">admin: {a.admin?.slice(0, 8)}{a.reason ? ` · reason: ${a.reason}` : ""}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
